/**
 * @fileoverview Store Pinia per la gestione dell'autenticazione
 *
 * Migrazione da store/auth.ts (Vuex) a stores/auth.ts (Pinia)
 * Mantiene la stessa interfaccia e funzionalità del modulo Vuex originale.
 *
 * @author Migrazione Vuex → Pinia
 * @since 2025-09-23
 */

import { defineStore } from 'pinia';
// Import temporaneo - verrà aggiornato quando migreremo completamente
// import { ROLES, ROLE_GROUPS, Role } from '@/lib/constants/roles'

// Import tipi centralizzati
import type { UserRoleOption, AuthState } from '@/lib/types/user';
import { ApplicationUserDto } from '~/lib/models/auth';
import { ROLES } from '~/lib/constants/roles';

// Re-export per compatibilità con codice esistente
export type { ApplicationUserDto, UserRoleOption, AuthState };

/**
 * Store Pinia per la gestione dell'autenticazione
 *
 * **Caratteristiche:**
 * - 🔄 **API compatibile** con il modulo Vuex esistente
 * - 🔒 **Persistenza sicura** con SecureLS (Fase 6)
 * - 🎯 **Stessi getter e actions** per facilità di migrazione
 * - 📱 **TypeScript** completamente tipizzato
 *
 * **Migrazione da Vuex:**
 * - `this.$store.getters['auth/isAuth']` → `useAuthStore().isAuth`
 * - `this.$store.getters['auth/userInfo']` → `useAuthStore().userInfo`
 * - `this.$store.commit('auth/SET_ACCESS_TOKEN', token)` → `useAuthStore().setAccessToken(token)`
 *
 * @example
 * ```typescript
 * // Usage in script setup:
 * import { useAuthStore } from '@/stores/auth'
 * import { storeToRefs } from 'pinia'
 *
 * const authStore = useAuthStore()
 * const { isAuth, userInfo, roles } = storeToRefs(authStore)
 *
 * // Actions
 * authStore.setAccessToken('jwt-token')
 * authStore.setUserInfo(userData)
 * ```
 */
export const useAuthStore = defineStore('auth', {
	/**
	 * Stato iniziale del store
	 * (Identico al modulo Vuex)
	 */
	state: (): AuthState => {
		return {
			accessToken: null,
			refreshToken: null,
			userInfo: null,
			impersonatedUserInfo: null,
			isAuthenticated: false,
			isLoading: false,
		};
	},

	/**
	 * Getters (computed properties reattive)
	 * Mantengono la stessa logica del modulo Vuex
	 */
	getters: {
		/**
		 * Indica se l'utente è autenticato
		 * @param state - State del store
		 * @returns True se l'utente ha un access token valido
		 */
		isAuth: (state): boolean => !!state.accessToken,

		/**
		 * Ruoli dell'utente corrente
		 * @param state - State del store
		 * @returns Array dei ruoli utente
		 */
		roles: (state): string[] => {
			return state.userInfo ? state.userInfo.roles || [] : [];
		},

		/**
		 * Opzioni utente per selettori (versione semplificata)
		 * @returns Array delle opzioni disponibili
		 */
		userOptions: (): UserRoleOption[] => [
			{ value: ROLES.SYS_ADMIN, label: ROLES.SYS_ADMIN, restrictedTo: [ROLES.SYS_ADMIN] },
			{ value: ROLES.ADMIN, label: ROLES.ADMIN, restrictedTo: [ROLES.ADMIN, ROLES.SYS_ADMIN] },
			{ value: ROLES.HR, label: ROLES.HR, restrictedTo: [ROLES.SYS_ADMIN] },
			{
				value: ROLES.MANAGER,
				label: ROLES.MANAGER,
				restrictedTo: [ROLES.MANAGER, ROLES.ADMIN, ROLES.SYS_ADMIN],
			},
			{
				value: ROLES.USER,
				label: ROLES.USER,
				restrictedTo: [ROLES.USER, ROLES.MANAGER, ROLES.ADMIN, ROLES.SYS_ADMIN],
			},
		],
	},

	/**
	 * Actions (metodi per modificare lo state)
	 * Sostituiscono le mutations di Vuex
	 */
	actions: {
		/**
		 * Imposta il token di accesso
		 * Sostituisce la mutation SET_ACCESS_TOKEN
		 *
		 * @param token - Token JWT o null per logout
		 */
		setAccessToken(token: string | null): void {
			this.accessToken = token;
		},

		/**
		 * Imposta il token di refresh
		 * Sostituisce la mutation SET_REFRESH_TOKEN
		 *
		 * @param token - Refresh token o null
		 */
		setRefreshToken(token: string | null): void {
			this.refreshToken = token;
		},

		/**
		 * Imposta le informazioni utente
		 * Sostituisce la mutation SET_USER_INFO
		 *
		 * @param user - Dati utente o null per logout
		 */
		setUserInfo(user: ApplicationUserDto | null): void {
			this.userInfo = user;
		},

		/**
		 * Imposta le informazioni dell'utente impersonificato
		 * Sostituisce la mutation SET_IMPERSONATED_USER_INFO
		 *
		 * @param user - Dati utente impersonificato o null
		 */
		setImpersonatedUserInfo(user: ApplicationUserDto | null): void {
			this.impersonatedUserInfo = user;
		},
	},

	/**
	 * Persistenza abilitata
	 */
	persist: {
		key: 'auth',
		paths: ['accessToken', 'refreshToken', 'userInfo', 'impersonatedUserInfo'],
	} as any,
});
